import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExt from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import { PreserveStyles, DivNode } from "./PreserveStyles";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Image as ImageIcon,
  Link2,
  Minus,
  Code2,
  Loader2,
  Palette,
} from "lucide-react";
import { Button } from "../../../../ui/button";
import { Separator } from "../../../../ui/separator";
import { cn } from "../../../../../lib/utils";
import { epicengageSupabasev1 } from "../../../../../integrations/supabase/client";
import { useMerchant } from "../../../../../hooks/useMerchantEngageV1";
import { useToast } from "../../../../../hooks/use-toast";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
};

const ToolbarButton = ({
  active,
  onClick,
  title,
  children,
  disabled,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={cn(
      "h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground transition-colors",
      "hover:bg-accent hover:text-accent-foreground",
      "disabled:opacity-50 disabled:pointer-events-none",
      active && "bg-accent text-accent-foreground",
    )}
  >
    {children}
  </button>
);

export const RichEmailEditor = ({ value, onChange, minHeight = 400 }: Props) => {
  const { merchant } = useMerchant();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceText, setSourceText] = useState(value || "");
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      LinkExt.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-primary underline" },
      }),
      Image.configure({ HTMLAttributes: { class: "max-w-full h-auto rounded" } }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: false, HTMLAttributes: { class: "border-collapse w-full" } }),
      TableRow,
      TableHeader,
      TableCell,
      DivNode,
      PreserveStyles,
    ],
    parseOptions: { preserveWhitespace: "full" },
    content: value || "",
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm max-w-none focus:outline-none px-4 py-3",
          "prose-headings:font-semibold prose-a:text-primary",
        ),
        style: `min-height: ${minHeight}px`,
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor || sourceMode) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", {
        emitUpdate: false,
        parseOptions: { preserveWhitespace: "full" },
        errorOnInvalidContent: false,
      });
    }
  }, [value, editor, sourceMode]);

  const enterSource = () => {
    if (!editor) return;
    setSourceText(value || editor.getHTML());
    setSourceMode(true);
  };

  const exitSource = () => {
    if (!editor) return;
    const nextHtml = sourceText || "<p></p>";
    editor.commands.setContent(nextHtml, {
      emitUpdate: false,
      parseOptions: { preserveWhitespace: "full" },
      errorOnInvalidContent: false,
    });
    onChange(editor.getHTML());
    setSourceMode(false);
  };

  const toggleSource = () => (sourceMode ? exitSource() : enterSource());

  const promptLink = () => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Enter URL", previous || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const handleImagePick = () => fileInputRef.current?.click();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editor) return;
    if (!merchant) {
      toast({ title: "No merchant", variant: "destructive" });
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast({ title: "Only image files allowed", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Image too large", description: "Max 5MB", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${merchant.id}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await epicengageSupabasev1.storage
        .from("email-assets")
        .upload(path, file, { cacheControl: "3600", upsert: false, contentType: file.type });
      if (upErr) throw upErr;
      const { data: pub } = epicengageSupabasev1.storage.from("email-assets").getPublicUrl(path);
      editor.chain().focus().setImage({ src: pub.publicUrl, alt: file.name }).run();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      toast({ title: "Image upload failed", description: msg, variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  if (!editor) return null;

  return (
    <div className="rounded-md border border-input bg-background overflow-hidden">
      <Toolbar
        editor={editor}
        sourceMode={sourceMode}
        toggleSource={toggleSource}
        onImageClick={handleImagePick}
        onLinkClick={promptLink}
        uploading={uploading}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageUpload}
      />

      {sourceMode ? (
        <textarea
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          className="w-full font-mono text-xs px-4 py-3 bg-background outline-none resize-y"
          style={{ minHeight }}
          spellCheck={false}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  );
};

const Toolbar = ({
  editor,
  sourceMode,
  toggleSource,
  onImageClick,
  onLinkClick,
  uploading,
}: {
  editor: Editor;
  sourceMode: boolean;
  toggleSource: () => void;
  onImageClick: () => void;
  onLinkClick: () => void;
  uploading: boolean;
}) => {
  const disabled = sourceMode;

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-muted/30">
      <ToolbarButton
        title="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={disabled}
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={disabled}
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={disabled}
      >
        <UnderlineIcon className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton
        title="Heading 1"
        active={editor.isActive("heading", { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        disabled={disabled}
      >
        <Heading1 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Heading 2"
        active={editor.isActive("heading", { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        disabled={disabled}
      >
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton
        title="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={disabled}
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={disabled}
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-5" />

      <Separator orientation="vertical" className="mx-1 h-5" />

      <label
        title="Text color"
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground cursor-pointer relative"
      >
        <Palette className="h-4 w-4" />
        <input
          type="color"
          className="absolute inset-0 opacity-0 cursor-pointer"
          disabled={disabled}
          value={(editor.getAttributes("textStyle").color as string) || "#000000"}
          onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        />
      </label>
      <ToolbarButton
        title="Clear color"
        onClick={() => editor.chain().focus().unsetColor().run()}
        disabled={disabled}
      >
        <span className="text-[10px] font-semibold">A×</span>
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton title="Insert image" onClick={onImageClick} disabled={disabled || uploading}>
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
      </ToolbarButton>
      <ToolbarButton
        title="Add link"
        active={editor.isActive("link")}
        onClick={onLinkClick}
        disabled={disabled}
      >
        <Link2 className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton
        title="Horizontal rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        disabled={disabled}
      >
        <Minus className="h-4 w-4" />
      </ToolbarButton>

      <div className="flex-1" />

      <Button
        type="button"
        variant={sourceMode ? "default" : "ghost"}
        size="sm"
        onClick={toggleSource}
        className="h-8 gap-1.5"
      >
        <Code2 className="h-4 w-4" />
        <span className="text-xs">{sourceMode ? "Visual" : "HTML"}</span>
      </Button>
    </div>
  );
};
